import { ExpandMore } from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import type React from "react";
import { Suspense, useState } from "react";
import { useGetApiAssetcategoriesSuspense } from "../../../api/rest/generated/asset-categories/asset-categories.ts";
import { useGetApiAssetscategoryattributesListSuspense } from "../../../api/rest/generated/assets-category-attributes/assets-category-attributes.ts";
import useDetail from "../../../hooks/useDetail.ts";
import { isEvyveska } from "../../../utills/iframeVariants.ts";

const getDetailDisplayValue = (key: string, value: unknown) => {
	if (typeof value === "boolean") {
		return value ? "Ano" : "Ne";
	}
	if (typeof value === "object" && DateTime.isDateTime(value)) {
		return value.setLocale("cs").toLocaleString(DateTime.DATE_MED); // Adjust the format as needed
	}
	if (!Number.isNaN(Number(value))) {
		return Number(value).toLocaleString("cs", {});
	}
	if (DateTime.fromISO(value as string).isValid) {
		switch (key.trim().toLowerCase()) {
			case "rok uvedení do provozu":
			case "rok výroby":
				return DateTime.fromISO(value as string).setLocale("cs").year;

			case "stk": {
				const date = DateTime.fromISO(value as string).setLocale("cs");

				return `${date.month}/${date.year}`;
			}
			default:
				return DateTime.fromISO(value as string)
					.setLocale("cs")
					.toLocaleString(DateTime.DATE_MED);
		}
	}
	return value;
};

const AttributesFallBack: React.FC = () => {
	return (
		<>
			<Skeleton />
			<Skeleton />
			<Skeleton />
			<Skeleton />
			<Skeleton />
			<Skeleton />
			<Skeleton />
			<Skeleton />
			<Skeleton />
			<Skeleton />
			<Skeleton />
		</>
	);
};

const lowercaseFirstLetter = (str: string): string => {
	if (str.length === 0) {
		return str;
	}
	return str.charAt(0).toLowerCase() + str.slice(1);
};

const Attributes: React.FC<{ id: number }> = ({ id }) => {
	const detail = useDetail(id);
	const categoriesQuery = useGetApiAssetcategoriesSuspense();
	const attributesQuery = useGetApiAssetscategoryattributesListSuspense();

	const categoryId = detail.data.assetsCategoryId;
	const categories = categoriesQuery.data.data ?? [];

	const category = categories.find((category) => category.id === categoryId);

	const attributesForCategory = category?.attributes ?? [];

	const attributes = attributesQuery.data.data ?? [];
	const detailAttributes = detail.data.attributes;

	const reducedAttributes = attributesForCategory
		.map((attributeName) => {
			const attribute = attributes.find((attr) => attr.name === attributeName);
			if (!attribute) return null;
			if (attribute.name === "DalsiInformace") return null;

			const detailAttributesKey: keyof typeof detailAttributes = lowercaseFirstLetter(
				attribute.name,
			) as keyof typeof detailAttributes;

			const getValue = () => {
				switch (attribute.type) {
					case "Integer":
					case "Double":
						return detailAttributes[detailAttributesKey] === 0 || !detailAttributes[detailAttributesKey]
							? undefined
							: detailAttributes[detailAttributesKey];
					default:
						return detailAttributes[detailAttributesKey];
				}
			};

			const value = getValue();

			return value !== undefined && value !== null
				? {
						label: attribute.displayLabel ?? attribute.name,
						value: value,
					}
				: null;
		})
		.filter((attribute): attribute is { label: string; value: string | number | boolean } => attribute !== null);

	const attributesPresent = reducedAttributes.length > 0;

	return (
		<Table
			size="small"
      sx={{
        margin: "auto",
        // make rows stack on small containers
        "& .MuiTableRow-root": {
          "@container ad-detail (max-width: 560px)": {
            display: "block",
            paddingY: 1,
            // borderBottom: theme => `1px solid ${theme.palette.divider}`,
          },
        },
        "& .MuiTableCell-root": {
          "@container ad-detail (max-width: 560px)": {
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            paddingX: .75,
          },
        },
        // first cell (label) styling on small
        "& .MuiTableRow-root .MuiTableCell-root:first-of-type": {
          color: "secondary.main",
          fontWeight: 600,
          "@container ad-detail (max-width: 560px)": {
            paddingTop: .5,
            paddingBottom: .5,
            border: 'none',

          },
        },
        // second cell (value) styling on small
        "& .MuiTableRow-root .MuiTableCell-root:last-of-type": {
          "@container ad-detail (max-width: 560px)": {
            paddingTop: .5,
            paddingBottom: .5,
            border: 'none',
          },
        },
        '& .MuiTableRow-root .MuiTableCell-root': {
          paddingTop: 1.5,
          paddingBottom: 1.5,
        },
        '& .MuiTableRow-root:last-child .MuiTableCell-root': {
          borderBottom: 'none',
        }
      }}
		>
			<TableBody>
				{!attributesPresent && (
					<TableRow
						sx={{
              "&:last-child td, &:last-child th": {border: 0},
              "&:hover": (
                isEvyveska()?
                  'none'
                  :
                  {
                    backgroundColor: "var(--mui-palette-action-hover)",
                  }
              )
            }}
					>
						<TableCell component="th" scope="row" sx={{ width: "100%" }}>
							Inzerát nemá vyplněné informace.
						</TableCell>
					</TableRow>
				)}
				{reducedAttributes.map((attribute) => {
					// Determine the display value based on the type of the value
					const displayValue = getDetailDisplayValue(attribute?.label ?? "", attribute?.value);

					return (
						<TableRow
							key={attribute?.label}
							sx={{
                "&:last-child td, &:last-child th": {border: 0},
                "&:hover": (
                  isEvyveska()?
                    'none'
                    :
                    {
                      backgroundColor: "var(--mui-palette-action-hover)",
                    }
                )
              }}
						>
							<TableCell component="th" scope="row" sx={{ width: "50%" }}>
								{attribute?.label}
							</TableCell>
							<TableCell
								sx={{ width: "50%", wordWrap: "break-word", overflowWrap: "break-word", wordBreak: "break-all" }}
							>
								<Typography sx={{ whiteSpace: "pre-wrap" }}>{`${displayValue}`}</Typography>
							</TableCell>
						</TableRow>
					);
				})}
			</TableBody>
		</Table>
	);
};

const labelMapping: Record<number, string> = {
	1: " o osobním vozidlu",
	2: " o užitkovém vozidlu",
	3: " o kolejovém vozidlu",
	4: " o sběratelském předmětu",
	5: "",
	6: " o výpočetní technice",
	7: "",
	8: " o mobilním telefonu",
};

const AttributesSection: React.FC<{ id: number }> = ({ id }) => {
	const [expandedAttributes, setExpandedAttributes] = useState<boolean>(true);
	return (
		<Accordion expanded={expandedAttributes}>
			<Tooltip
				followCursor={true}
				placement="right"
				title={
					expandedAttributes
						? // ${labelMapping[id]
							"Skrýt informace"
						: "Zobrazit informace"
				}
			>
				<AccordionSummary
					onClick={() => setExpandedAttributes(!expandedAttributes)}
					sx={{ backgroundColor: "grey.200" }}
					expandIcon={<ExpandMore fontSize="medium" color="secondary" />}
				>
					<Typography variant="subtitle1" component="div" sx={{ fontWeight: "bolder", color: "info.main" }}>
						Informace
					</Typography>
				</AccordionSummary>
			</Tooltip>
			<AccordionDetails>
				<Suspense fallback={<AttributesFallBack />}>
					<Attributes id={id} />
				</Suspense>
			</AccordionDetails>
		</Accordion>
	);
};

export default AttributesSection;
